import {BehaviorSubject, Subscription, timer} from 'rxjs';
import {AudioPlayerService} from '../../utils/audio-player.service';
import {NotificationService} from '../../../../shared/notification.service';
import {SecureLocalService} from '../../../../core/secure-local.service';
import {IMessage} from './model/IMessage';
import {AppUpdateService} from '../../../../core/app-update.service';
import {EventEmitter, Injectable, Output} from '@angular/core';
import {connect, Socket} from 'socket.io-client';
import {ISessionStatus} from './model/ISessionStatus';
import {IUser} from '../../../../users/users';
import {IRequestCall} from './model/IRequestCall';
import {MediasoupService} from '../../service/wss.mediasoup';
import {marker} from '@biesbjerg/ngx-translate-extract-marker';
import {TranslateService} from '@ngx-translate/core';
import {Router} from '@angular/router';
import {VibaQueueService} from '../../service/viba-queue.service';
import {CallEventType} from './model/IAgentEvent';
import {IEndpoint} from '../../../admin/endpoint/endpoints';
import {UserMessage} from './agent-chat-component/chat-component.interface';

type IOSocket = Socket & { request: (ioEvent: string, data?: any) => Promise<any> };

@Injectable({
  providedIn: 'root',
})

export class CommunicationService {
  // public session;
  public missedCalls = 0;
  currentEndpoint: IEndpoint;
  private currentEndpointId;

  insideCall: BehaviorSubject<any> = new BehaviorSubject<any>({type: 'login', status: false});
  switchChatToCall: BehaviorSubject<string> = new BehaviorSubject<string>('not-requested');
  chatToggleSubscription: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  sessionStarted: BehaviorSubject<any> = new BehaviorSubject<any>(0);
  playContent: BehaviorSubject<IMessage> = new BehaviorSubject<IMessage>(null);
  requestingToSwitchChatToCall = false;
  mediaSoup: MediasoupService;
  isSwitchedChatToCall = false;
  private timeoutSubscription: Subscription;

  constructor(protected audioPlayer: AudioPlayerService,
              private notificationService: NotificationService,
              private secureService: SecureLocalService,
              private vibaQueueService: VibaQueueService,
              private router: Router,
              private translateService: TranslateService,
              private appUpdateService: AppUpdateService) {
  }

  public socket: IOSocket;
  public callRequest: IRequestCall;
  public status: ISessionStatus;

  @Output() messageReceived: EventEmitter<any> = new EventEmitter();
  @Output() shareChatMessagesWithAgent: EventEmitter<any> = new EventEmitter<any>();

  public connect(current_user_id: string) {
    this.socket = connect(this.getAgentData().queueServerUrl, {
      query: {
        sessionId: current_user_id,
        userId: current_user_id,
        fullName: this.getAgentData().userName,
        userType: 'agent',
        device: 'WEB',
        queues: this.getAgentData().queues,
      },
      transports: ['websocket'],
    }) as IOSocket;

    this.socket.request = (ioEvent: string, data: object = {}) => {
      return new Promise((resolve) => {
        if (this.socket) {
          this.socket.emit(ioEvent, data, resolve);
        }
      });
    };

    this.socket.on('connect', () => {
      if (!this.secureService.tokenExpired()) {
        console.log('WEB AGENT CONNECTED...');
      } else {
        this.socket?.disconnect();
        this.socket = null;
        this.audioPlayer.stop('ringing');
        this.resetServiceParams();
        console.log('Session expired web agent!!');
        this.router.navigate(['login']);
      }
    });

    this.socket.on('disconnect', (reason) => {
      if (!this.secureService.tokenExpired()) {
        console.log('WEB AGENT DISCONNECTED : ' + reason);
      } else {
        this.socket?.disconnect();
        this.socket = null;
        this.audioPlayer.stop('ringing');
        this.resetServiceParams();
        console.log('Session expired web agent!');
        this.router.navigate(['login']);
      }
    });

    this.socket.on('ring', async (msg: IRequestCall) => {
      console.log(msg);
      if (this.status !== ISessionStatus.STATUS_ANSWERED && this.status !== ISessionStatus.STATUS_CONFIRMED) {
        this.status = ISessionStatus.STATUS_WAITING_FOR_ANSWER;
        this.callRequest = msg;
        this.sessionStarted.next(msg.customerId);
        this.audioPlayer.play('ringing', 1, true);
        this.notificationService.callAlert();
      }
      if (!msg.requestingUserId) {
        this.timeoutSubscription = timer(12000).subscribe(() => {
          this.callTimeout();
        });
      } else {
        this.timeoutSubscription = timer(25000).subscribe(() => {
          this.callTimeout();
        });
      }
    });

    this.socket.on('agentDisconnect', async () => {
      console.log('agentDisconnect');
      this.notificationService.error(this.translateService.stream(marker('This user has started another session. You will be shortly disconnected!')));
      localStorage.removeItem('authToken');
      this.socket?.disconnect();
      this.status = ISessionStatus.STATUS_TERMINATED;
      this.router.navigate(['login']);
    });

    this.socket.on('agentTimeout', async () => {
      console.log('agentTimeout');
      this.notificationService.error(this.translateService.stream(marker('The agent has been logged out due to inactivity!')));
      localStorage.removeItem('authToken');
      this.socket?.disconnect();
      this.status = ISessionStatus.STATUS_TERMINATED;
      this.router.navigate(['login']);
    });
  }

  private async callTimeout() {
    this.callRequest.orgId = localStorage.getItem('selectedOrganization');
    this.callRequest.endpointId = this.currentEndpointId;
    await this.socket.request(CallEventType.CALL_UNANSWERED, this.callRequest);
    if (this.status === ISessionStatus.STATUS_WAITING_FOR_ANSWER) {
      this.updateStatus(false, 'reject', null);
      this.audioPlayer.stop('ringing');
      this.audioPlayer.play('rejected', 1, false);
      this.sessionStarted.next(-1);
      this.status = ISessionStatus.STATUS_CANCELED;
      this.checkMissedCallCount();
      this.appUpdateService.canReloadApp = true;
      this.callRequest = null;
    }
  }

  public setCurrentEndpoint(endpoint: IEndpoint) {
    this.currentEndpoint = endpoint;
    this.currentEndpointId = endpoint.id;
  }

  public getCurrentEndpointId(){
    return this.currentEndpointId;
  }

  registerRoomClosed(mediaSoup: MediasoupService) {
    this.mediaSoup = mediaSoup;
    this.socket.on('roomClose', async () => {
      console.log('roomClose called');
      this.terminateSession();
      mediaSoup?.close().then(() => {
        this.socket?.disconnect();
      });
      this.insideCall.next({type: 'ROOM_CLOSED', status: false});
    });

    this.socket.on('mediaDisconnectMember', async () => {
      console.log('mediaDisconnectMember called');
    });

    this.socket.on('connect_timeout', async () => {
      console.log('connect_timeout');
      mediaSoup?.close();
      this.socket?.disconnect();
      this.terminateSession();
    });

    this.socket.on('connect_error', async () => {
      console.log('connect_error');
      mediaSoup?.close();
      this.socket?.disconnect();
      this.terminateSession();
    });


  }


  public videoCallInit() {
    this.status = ISessionStatus.STATUS_NULL;
  }

  public getAgentData() {
    return this.secureService.geItem('web-rtc-agent-data');
  }

  public makeUserAvailable(crossCall: boolean) {
    if ((!this.socket || !this.socket.connected)) {
      this.connect(CommunicationService.getCurrentUser().userId + '');
    }
    this.missedCalls = 0;
  }


  public makeUserUnAvailable(crossCall: boolean) {
    this.socket?.disconnect();
  }

  sendWebSocketMessages(message: IMessage) {
    console.log('Sending Message \n' + JSON.stringify(message));
    this.playContent.next(message);
    this.socket.request(CallEventType.BROADCAST_MESSAGE, {
      data: message,
    }).then(() => console.log('successfully')).catch((e) => console.log('error' + e));
  }


  public updateStatus(status: boolean, type: string, roomId: string) {
    this.insideCall.next({type: type, status: status, roomId: roomId});
  }

  answer() {
    if (this.timeoutSubscription) {
      this.timeoutSubscription.unsubscribe();
    }
    this.callRequest.endpointId = this.currentEndpointId;
    this.callRequest.orgId =  localStorage.getItem('selectedOrganization');
    this.socket.request(CallEventType.CALL_ANSWER_QUEUE, this.callRequest).then(() => {
      console.log('Called Answered queue confirmation received');
      this.socket?.disconnect();
      this.connectToSFU();

      this.socket.request(CallEventType.CALL_ANSWER_SFU, this.callRequest).then(() => {
        console.log('Called Answered sfu');
      });

      this.missedCalls = 0;
      this.status = ISessionStatus.STATUS_ANSWERED;
      this.updateStatus(true, 'answer', this.callRequest.room);
      this.audioPlayer.stop('ringing');
      this.audioPlayer.play('answered', 1, false);
    });
  }

  private connectToSFU() {
    this.socket = connect('https://' + this.callRequest.hostname, {
      query: {
        session_id: this.callRequest.room,
        user_id: CommunicationService.getCurrentUser().userId,
        user_type: 'agent',
        device: 'browser',
        queue: this.callRequest.queue,
        orgId: localStorage.getItem('selectedOrganization'),
        endpointId : this.currentEndpointId,
        customerId : this.callRequest.customerId
      },
      transports: ['websocket'],
    }) as IOSocket;

    this.socket.request = (ioEvent: string, data: object = {}) => {
      return new Promise((resolve) => {
        if (this.socket) {
          this.socket.emit(ioEvent, data, resolve);
        }
      });
    };

    this.socket.request(CallEventType.ROOM_EXISTS, {session_id: this.callRequest.room}).then((roomExists) => {
      if (!roomExists) {
        this.notificationService.error(this.translateService.stream(marker('Client is no longer connected')));
        this.terminateSession();
        // this.sessionStarted.next(-1);
        this.insideCall.next({type: 'CLIENT_NO_LONGER_CONNECTED', status: false});
      }
    });

    this.socket.on('send', async (data: { type: string; data: string, user: string, userType: string }) => {
      this.messageReceived.emit(data);
    });

    this.socket.on('switchChatToCall', async () => {
      console.log('switchChatToCall');
      this.requestingToSwitchChatToCall = true;
    });

    this.socket.on('shareChatMessagesWithAgent', async (data: {messages: UserMessage[]}) => {
      console.log('shareChatMessagesWithAgent');
      this.shareChatMessagesWithAgent.emit(data.messages);
    });

    this.socket.on('transferRejected', async () => {
      console.log('transferRejected');
      this.notificationService.error(this.translateService.stream(marker('Agent denied the call transfer')));
    });

    this.socket.on('transferAgentNotAvailable', async () => {
      console.log('transferAgentNotAvailable');
      this.notificationService.error(this.translateService.stream(marker('Agent not available to transfer')));
    });

    this.socket.on('connect', () => {
      console.log('SFU CONNECTED : ')
    });

    this.socket.on('disconnect', (reason) => {
      console.log('SFU DISCONNECTED : ' + reason);
      this.terminateSession();
      this.mediaSoup?.producerVideoClose().then(() => {
          console.log('produce video close');
        }
      );
      this.mediaSoup?.producerAudioClose().then(() => {
          console.log('produce audio close');
        }
      );
      this.mediaSoup?.close().then(() => {
        this.socket?.disconnect();
      });
      this.insideCall.next({type: 'ROOM_CLOSED', status: false});
    });
  }


  public static getCurrentUser(): IUser {
    return JSON.parse(localStorage.getItem('currentUser'));
  }

  terminateSession() {
    this.socket?.disconnect();
    this.status = ISessionStatus.STATUS_TERMINATED;
    this.audioPlayer.stop('ringing');
    if (this.callRequest) {
      this.updateStatus(false, 'terminate', this.callRequest.room);
    }
  }

  async rejectCall() {
    this.callRequest.orgId = localStorage.getItem('selectedOrganization');
    this.callRequest.endpointId = this.currentEndpointId;
    await this.socket.request(CallEventType.CALL_REJECT, this.callRequest);


    if (this.timeoutSubscription) {
      this.timeoutSubscription.unsubscribe();
    }
    this.updateStatus(false, 'reject', this.callRequest.room);
    this.audioPlayer.stop('ringing');
    this.audioPlayer.play('rejected', 1, false);
    this.status = ISessionStatus.STATUS_CANCELED;
    this.checkMissedCallCount();
    this.sessionStarted.next(-1);
    this.appUpdateService.canReloadApp = true;
  }

  private checkMissedCallCount() {
    this.missedCalls++;
    if (this.missedCalls >= this.getAgentData().missedCalls) {
      this.insideCall.next({type: 'MAX_MISSED_EXCEED', status: false});
      this.makeUserUnAvailable(false);
    }
  }

  logout() {
    console.log('logout web');
    if (this.status === ISessionStatus.STATUS_CONFIRMED) {
      this.status = ISessionStatus.STATUS_TERMINATED;
      this.insideCall.next(false);
      this.mediaSoup?.close();
      this.socket?.disconnect();
    } else {
      this.mediaSoup?.close();
      this.socket?.disconnect();
    }
    this.audioPlayer.stop('ringing');
    this.resetServiceParams();
  }

  private resetServiceParams() {
    this.socket = null;
    this.missedCalls = 0;
    this.currentEndpointId = null;
    this.callRequest = null;
    this.status = null;
    this.insideCall = new BehaviorSubject<any>({type: 'login', status: false});
    this.sessionStarted = new BehaviorSubject<any>(0);
    this.playContent = new BehaviorSubject<IMessage>(null);
    this.timeoutSubscription = null;
    this.mediaSoup = null;
  }

  transferToAnotherAgent(agentId: string) {
    this.socket.request(CallEventType.CALL_TRANSFER_AGENT, {
      room: this.callRequest.room,
      userId: agentId,
      queue: this.callRequest.queue,
      customerId: this.callRequest.customerId,
      endpointId: this.currentEndpointId,
      orgId:  this.callRequest.orgId,
    }).then(() => console.log('Call transferred to agent'));
    this.notificationService.info(this.translateService.stream(marker('Call transferred to an agent')));
  }

  async transferToQueue(queue: string) {
    console.log('Call Transfer to Queue : ' + queue);
    await this.socket.request(CallEventType.CALL_TRANSFER_QUEUE, {
      room: this.callRequest.room,
      userId: CommunicationService.getCurrentUser().userId,
      queue: queue,
      customerId: this.callRequest.customerId,
      endpointId: this.currentEndpointId,
      orgId:  this.callRequest.orgId
    }).then(() => console.log('Call Transferred to queue: ' + queue));
    this.notificationService.info(this.translateService.stream(marker('Call transferred to a queue: ' + queue)));
  }

  public submitReport(visitId: number) {
    this.callRequest.formVisitId = visitId,
    this.callRequest.endpointId = this.currentEndpointId,
    this.vibaQueueService.submitReport(this.callRequest);
  }

  public sendMessage(message: string): void {
    this.socket?.request('media', {action: 'send', data: {type: 'string', data: message}});
  }

  browserClosed() {
    this.vibaQueueService.agentLogoutClosed(CommunicationService.getCurrentUser().userId);
  }

}
